import React, { type PropsWithChildren, useContext } from "react";
import useSWRImmutable from "swr/immutable";
import { useAuthContext } from "../auth/context";
import { type AlbelliDocument, fetchAlbelliDocument } from "./albelli";

export type AlbelliContext = {
  albelliDocument: AlbelliDocument | undefined;
};

export const albelliContext = React.createContext<AlbelliContext | undefined>(
  undefined,
);

export type AlbelliContextProviderProps = {
  documentId?: string;
};

export function AlbelliContextProvider(
  props: PropsWithChildren<AlbelliContextProviderProps>,
) {
  const authContext = useAuthContext();

  const { data, error, isLoading } = useSWRImmutable(
    // https://swr.vercel.app/docs/conditional-fetching#conditional
    () => props.documentId,
    (docId: string) =>
      fetchAlbelliDocument(docId, authContext.authorizationHeader),
    {
      shouldRetryOnError: false,
    },
  );

  if (isLoading) {
    return <></>;
  } else if (error) {
    throw new Error(`Error fetching Albelli document: ${error}`);
  }

  return (
    <albelliContext.Provider value={{ albelliDocument: data }}>
      {props.children}
    </albelliContext.Provider>
  );
}

export function useAlbelliContext() {
  const context = useContext(albelliContext);
  if (!context) {
    throw new Error(
      "useAlbelliContext must be used with AlbelliContextProvider",
    );
  }
  return context;
}
